import Plausible from 'plausible-tracker'
import { useEffect, useLayoutEffect, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useAppWindowFocus, useEvents, useMixPanel } from '@/features/app/hooks'
import { appState } from '@/features/app/store'
import { userData } from '@/features/user/store'
import { RootNavigator } from '@/routes'
import { BookingReportModal } from './components/Elements/Modals/BookingReportModal'
import { authAdminPA } from './features/auth/store'
import { useUser } from './features/user'
import { usePopup } from './hooks'

export const initializeTagManager = () => {
  const tagManagerArgs = {
    gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER || '',
  }
  TagManager.initialize(tagManagerArgs)
}

export const initializePlausible = () => {
  if (process.env.REACT_APP_PLAUSIBLE_SEND === 'true') {
    const plausible = Plausible({
      domain: 'babysisters.pt',
    })
    plausible.enableAutoPageviews()
  }
}

export const initializeZendesk = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.zE('messenger', 'hide')
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.zE('messenger:on', 'close', function () {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.zE('messenger', 'hide')
  })
}

function App() {
  const { i18n } = useTranslation()
  const dispatch = useDispatch()
  const theme = useMantineTheme()
  const isSmallerThenMd = useMediaQuery(`(max-width: ${theme.breakpoints.md}px)`)
  const event = useEvents()
  const { user, isBabysitter } = useUser()
  const isFrame = window.self !== window.top
  const [searchParams, setSearchParams] = useSearchParams()
  const { visible, open, close } = usePopup()
  const [pendingReport, setPendingReport] = useState<number>(0)
  useAppWindowFocus()
  useMixPanel()
  initializeZendesk()

  useEffect(() => {
    if (user && isBabysitter()) {
      if (pendingReport == 0) {
        if (user.pending_booking_report && user.pending_booking_report.length > 0) {
          open(true)
        }
      }
      setPendingReport(user.pending_booking_report?.length || 0)
    }
  }, [user?.pending_booking_report])

  useEffect(() => {
    if (!isFrame) {
      initializeTagManager()
      initializePlausible()
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.zE('messenger:set', 'locale', i18n.language)
  }, [])

  useLayoutEffect(() => {
    if (!isFrame) event.actions.appLoaded({})
    const token = searchParams.get('token')
    if ((isFrame || process.env.REACT_APP_IGNORE_FRAME_PATHS_CHECK === 'true') && token) {
      setAdminToken(token)
    } else {
      setAdminToken(undefined)
    }
  }, [])

  const setAdminToken = async (token: string | undefined) => {
    await dispatch(
      authAdminPA.request({
        token: token,
      })
    )
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.zE('messenger:set', 'locale', i18n.language)
  }, [i18n.language])

  useEffect(() => {
    dispatch(appState.mobileView(isSmallerThenMd))
  }, [isSmallerThenMd])

  const onReportSubmitted = () => {
    dispatch(userData.remove1UnreadMessages())
  }

  return (
    <>
      <RootNavigator />
      <BookingReportModal
        visible={visible}
        onLoginModalClose={close}
        onSaveReport={onReportSubmitted}
        bookingReportId={
          user?.pending_booking_report && user?.pending_booking_report?.length > 0
            ? user?.pending_booking_report[0].id
            : undefined
        }
        bookingReportClient={
          user?.pending_booking_report && user?.pending_booking_report?.length > 0
            ? user?.pending_booking_report[0].client
            : ''
        }
        bookingReportChildren={
          user?.pending_booking_report && user?.pending_booking_report?.length > 0
            ? user?.pending_booking_report[0].children_names
            : []
        }
      />
    </>
  )
}

export default App
